import { useState, useEffect, useMemo, useRef } from "react";
import {
  ArrowLeft,
  CheckCircle2,
  Loader2,
  AlertTriangle,
  Download,
  RefreshCw,
  Plus,
  Scale,
  GitCompareArrows,
  Trash2,
  ListChecks,
} from "lucide-react";
import { apiv2 } from "../apiv2";
import { filenameTimestamp } from "./dataConverterView.utils";
import {
  ConnectDatabaseDialog,
  type ConnectResult,
  type DbConnectFormValues,
} from "./data-source/ConnectDatabaseDialog";
import SavedConnectionsPanel from "./SavedConnectionsPanel";
import { SectionCard } from "./DataConverterView";
import type {
  TableMeta,
  TableObjectMapping,
  TableCountResult,
  ReconcileRowResponse,
  RowMismatchDetail,
} from "../apiv2";

// 2026-10-15, requested by user: a 3rd option on Reconciliation's "which
// mapping should this use?" gate for projects with no STTM mapping at all
// — connect directly to a source and target system, hand-define the key
// field(s) and the fields to compare, and run the same counts/columns/rows
// reconciliation engines the mapping-driven flow uses. Those backend
// endpoints already accept a generic TableObjectMapping with no STTM-
// pipeline dependency, so this builds a minimal synthetic one client-side
// instead of reading one from the project's mapping history. Session-only
// — nothing here is persisted server-side, same as ad-hoc row-reconciliation
// results elsewhere on this page. Pulled into its own component (rather
// than inlined in the already-enormous DataConverterView) so its field-
// list state stays scoped to just this panel.
interface StandaloneReconcilePanelProps {
  projectId?: string;
  sourceConnectionId: string | null;
  sourceConnLabel: string;
  targetConnectionId: string | null;
  targetConnLabel: string;
  handleSourceConnectNew: (
    values: DbConnectFormValues,
  ) => Promise<ConnectResult>;
  handleSourceConnectExistingForLoad: (
    connectionId: string,
    extraFieldValue?: string,
  ) => Promise<ConnectResult>;
  handleSourceConnectionActivatedFromLibrary: () => void;
  handleTargetConnectNew: (
    values: DbConnectFormValues,
  ) => Promise<ConnectResult>;
  handleTargetConnectExisting: (
    connectionId: string,
    extraFieldValue?: string,
  ) => Promise<ConnectResult>;
  handleTargetConnectionActivatedFromLibrary: () => void;
  onBackToMappingChoice: () => void;
}

interface StandaloneReconcileField {
  id: string;
  sourceColumn: string;
  targetField: string;
  isKey: boolean;
  // When true, show a free-text input for this one field even though a
  // column list was successfully scanned for its table (e.g. a computed
  // comparison expression, or a column the scan didn't surface).
  sourceManual?: boolean;
  targetManual?: boolean;
}

export function StandaloneReconcilePanel({
  projectId,
  sourceConnectionId,
  sourceConnLabel,
  targetConnectionId,
  targetConnLabel,
  handleSourceConnectNew,
  handleSourceConnectExistingForLoad,
  handleSourceConnectionActivatedFromLibrary,
  handleTargetConnectNew,
  handleTargetConnectExisting,
  handleTargetConnectionActivatedFromLibrary,
  onBackToMappingChoice,
}: StandaloneReconcilePanelProps) {
  const [showSourceConnectDialog, setShowSourceConnectDialog] = useState(false);
  const [showTargetConnectDialog, setShowTargetConnectDialog] = useState(false);
  const fieldIdRef = useRef(0);
  const nextFieldId = () => `sf-${fieldIdRef.current++}`;
  const [srcSchema, setSrcSchema] = useState("");
  const [srcTable, setSrcTable] = useState("");
  const [tgtSchema, setTgtSchema] = useState("");
  const [tgtTable, setTgtTable] = useState("");
  const [fields, setFields] = useState<StandaloneReconcileField[]>([
    { id: nextFieldId(), sourceColumn: "", targetField: "", isKey: true },
  ]);
  const [countsResult, setCountsResult] = useState<TableCountResult | null>(
    null,
  );
  const [rowsResult, setRowsResult] = useState<ReconcileRowResponse | null>(
    null,
  );
  // Only one of counts/rows is shown at a time -- set to whichever action
  // was last run (a prior session's stale result stays available via its
  // tab, but isn't shown stacked on top of a newer one).
  const [activeResultTab, setActiveResultTab] = useState<
    "counts" | "rows" | null
  >(null);
  const [countsLoading, setCountsLoading] = useState(false);
  const [rowsLoading, setRowsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Schema/table/column pickers -- scanned live from the connected source
  // and target databases so the user can choose from a dropdown instead of
  // typing identifiers by hand. Each falls back to manual text entry when
  // nothing has been listed yet (not connected, or list came back empty).
  const [srcSchemas, setSrcSchemas] = useState<string[]>([]);
  const [srcSchemasLoading, setSrcSchemasLoading] = useState(false);
  const [srcSchemaManual, setSrcSchemaManual] = useState(false);
  const [srcTables, setSrcTables] = useState<TableMeta[]>([]);
  const [srcTablesLoading, setSrcTablesLoading] = useState(false);
  const [srcTableManual, setSrcTableManual] = useState(false);

  const [tgtSchemas, setTgtSchemas] = useState<string[]>([]);
  const [tgtSchemasLoading, setTgtSchemasLoading] = useState(false);
  const [tgtSchemaManual, setTgtSchemaManual] = useState(false);
  const [tgtTables, setTgtTables] = useState<TableMeta[]>([]);
  const [tgtTablesLoading, setTgtTablesLoading] = useState(false);
  const [tgtTableManual, setTgtTableManual] = useState(false);

  const srcColumns = useMemo(
    () => srcTables.find((t) => t.table_name === srcTable)?.columns ?? [],
    [srcTables, srcTable],
  );
  const tgtColumns = useMemo(
    () => tgtTables.find((t) => t.table_name === tgtTable)?.columns ?? [],
    [tgtTables, tgtTable],
  );

  const resetSrcListingsFor = (connectionId: string | null) => {
    setSrcSchemas([]);
    setSrcSchemaManual(!connectionId);
    setSrcTables([]);
    setSrcTableManual(!connectionId);
  };
  const resetTgtListingsFor = (connectionId: string | null) => {
    setTgtSchemas([]);
    setTgtSchemaManual(!connectionId);
    setTgtTables([]);
    setTgtTableManual(!connectionId);
  };

  // Re-list whenever the connection itself changes (connect, switch to a
  // different saved connection, etc.) -- stale schema/table lists from a
  // previous connection would otherwise silently point at the wrong DB.
  useEffect(() => {
    resetSrcListingsFor(sourceConnectionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sourceConnectionId]);
  useEffect(() => {
    resetTgtListingsFor(targetConnectionId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [targetConnectionId]);

  const handleListSrcSchemas = async () => {
    if (!sourceConnectionId) return;
    setSrcSchemasLoading(true);
    try {
      const res = await apiv2.converterSourceConnect({
        connectionId: sourceConnectionId,
        projectId,
      });
      const list = res.schemas ?? [];
      setSrcSchemas(list);
      setSrcSchemaManual(list.length === 0 || !list.includes(srcSchema));
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Failed to list source schemas",
      );
    } finally {
      setSrcSchemasLoading(false);
    }
  };

  const handleListSrcTables = async (schemaName: string) => {
    if (!sourceConnectionId) return;
    setSrcTablesLoading(true);
    try {
      const res = await apiv2.converterSourceScan(
        { connectionId: sourceConnectionId, projectId },
        schemaName || undefined,
      );
      const list = res.tables ?? [];
      setSrcTables(list);
      setSrcTableManual(
        list.length === 0 || !list.some((t) => t.table_name === srcTable),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to list source tables");
    } finally {
      setSrcTablesLoading(false);
    }
  };

  const handleListTgtSchemas = async () => {
    if (!targetConnectionId) return;
    setTgtSchemasLoading(true);
    try {
      const res = await apiv2.converterTargetConnect({
        connectionId: targetConnectionId,
        projectId,
      });
      const list = res.schemas ?? [];
      setTgtSchemas(list);
      setTgtSchemaManual(list.length === 0 || !list.includes(tgtSchema));
    } catch (e) {
      setError(
        e instanceof Error ? e.message : "Failed to list target schemas",
      );
    } finally {
      setTgtSchemasLoading(false);
    }
  };

  const handleListTgtTables = async (schemaName: string) => {
    if (!targetConnectionId) return;
    setTgtTablesLoading(true);
    try {
      const res = await apiv2.converterTargetScan(
        { connectionId: targetConnectionId, projectId },
        schemaName || undefined,
      );
      const list = res.tables ?? [];
      setTgtTables(list);
      setTgtTableManual(
        list.length === 0 || !list.some((t) => t.table_name === tgtTable),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to list target tables");
    } finally {
      setTgtTablesLoading(false);
    }
  };

  const buildMapping = (): {
    mapping: TableObjectMapping;
    pair: {
      srcSchema: string;
      srcTable: string;
      tgtSchema: string;
      tgtTable: string;
    };
    validFields: StandaloneReconcileField[];
  } => {
    const validFields = fields.filter(
      (f) => f.sourceColumn.trim() && f.targetField.trim(),
    );
    const mapping: TableObjectMapping = {
      source_table: srcTable.trim(),
      source_schema: srcSchema.trim(),
      target_object: tgtTable.trim(),
      target_label: tgtTable.trim(),
      confidence: 1,
      field_mappings: validFields.map((f) => ({
        source_column: f.sourceColumn.trim(),
        source_type: "string",
        target_field: f.targetField.trim(),
        target_type: "string",
        transformation: "",
        validation_rule: "",
        notes: "",
        confidence: 1,
        status: "Mapped",
      })),
    };
    const pair = {
      srcSchema: srcSchema.trim(),
      srcTable: srcTable.trim(),
      tgtSchema: tgtSchema.trim(),
      tgtTable: tgtTable.trim(),
    };
    return { mapping, pair, validFields };
  };

  const validateSetup = (): string | null => {
    if (!sourceConnectionId || !targetConnectionId) {
      return "Connect both a source and a target system first.";
    }
    if (!srcTable.trim() || !tgtTable.trim()) {
      return "Enter both a source table and a target table.";
    }
    const { validFields } = buildMapping();
    if (validFields.length === 0) {
      return "Add at least one field pair to compare.";
    }
    return null;
  };

  const handleReconcileCounts = async () => {
    const err = validateSetup();
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setCountsLoading(true);
    try {
      const { mapping, pair } = buildMapping();
      const res = await apiv2.converterReconcileCounts({
        sourceConnectionRef: { connectionId: sourceConnectionId!, projectId },
        targetConnectionRef: { connectionId: targetConnectionId!, projectId },
        mappings: [mapping],
        targetSchema: tgtSchema.trim() || "MIGRATION",
        pairs: [pair],
        projectId,
      });
      setCountsResult(res.results[0] ?? null);
      setActiveResultTab("counts");
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : "Count check failed");
    } finally {
      setCountsLoading(false);
    }
  };

  const handleReconcileRows = async () => {
    const err = validateSetup();
    if (err) {
      setError(err);
      return;
    }
    const keyColumns = fields
      .filter((f) => f.isKey && f.targetField.trim())
      .map((f) => f.targetField.trim());
    if (keyColumns.length === 0) {
      setError("Mark at least one field as a key field to match rows.");
      return;
    }
    setError(null);
    setRowsLoading(true);
    try {
      const { mapping, pair } = buildMapping();
      const res = await apiv2.converterReconcileRows({
        sourceConnectionRef: { connectionId: sourceConnectionId!, projectId },
        targetConnectionRef: { connectionId: targetConnectionId!, projectId },
        mapping,
        targetSchema: tgtSchema.trim() || "MIGRATION",
        pair,
        keyColumns,
        projectId,
      });
      setRowsResult(res);
      setActiveResultTab("rows");
    } catch (e: unknown) {
      setError(
        e instanceof Error ? e.message : "Row-by-row reconciliation failed",
      );
    } finally {
      setRowsLoading(false);
    }
  };

  const csvEscape = (v: string | number) => {
    const s = String(v);
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };

  // Download #1: every source record whose key didn't match a target row
  // (rowsResult.missing_in_target_rows — capped per
  // _ROW_RECONCILE_MAX_MISSING_DETAILS, noted in the filename/header when
  // truncated so the report never silently under-represents the real count
  // in rowsResult.missing_in_target).
  const downloadMissingInTargetCsv = () => {
    if (!rowsResult) return;
    const valueCols = fields
      .filter((f) => !f.isKey && f.targetField.trim())
      .map((f) => f.targetField.trim());
    const header = [...rowsResult.key_columns, ...valueCols];
    const rows = rowsResult.missing_in_target_rows.map((r) => [
      ...rowsResult.key_columns.map((k) => r.key[k] ?? ""),
      ...valueCols.map((c) => r.values[c] ?? ""),
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `missing_in_target_${filenameTimestamp()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Download #2: for every compared (non-key) field, how many of the
  // common-key rows (matched_rows + mismatched_rows) have the same value on
  // both sides vs a different one — rowsResult.field_stats, an exact count
  // across every common row, not just the capped mismatch-detail sample.
  const downloadFieldStatsCsv = () => {
    if (!rowsResult) return;
    const header = [
      "Source Column",
      "Target Field",
      "Matching Records",
      "Not Matching Records",
      "Match Rate %",
    ];
    const rows = rowsResult.field_stats.map((s) => {
      const total = s.matched_count + s.mismatched_count;
      const rate =
        total > 0 ? ((s.matched_count / total) * 100).toFixed(2) : "N/A";
      return [
        s.source_column,
        s.target_field,
        s.matched_count,
        s.mismatched_count,
        rate,
      ];
    });
    const csv = [header, ...rows]
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `field_match_report_${filenameTimestamp()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Download #3: every common-key row where at least one compared field
  // differs — one row per (key, differing column) pair, source and target
  // value side by side (rowsResult.mismatches — capped per
  // _ROW_RECONCILE_MAX_MISMATCH_DETAILS).
  const downloadMismatchesCsv = () => {
    if (!rowsResult) return;
    const header = [
      ...rowsResult.key_columns,
      "Field",
      "Source Value",
      "Target Value",
    ];
    const rows = rowsResult.mismatches.flatMap((m: RowMismatchDetail) =>
      m.differing_columns.map((d) => [
        ...rowsResult.key_columns.map((k) => m.key[k] ?? ""),
        d.target_field,
        d.source_value ?? "",
        d.target_value ?? "",
      ]),
    );
    const csv = [header, ...rows]
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `mismatches_report_${filenameTimestamp()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Download #4: every target record whose key didn't match a source row
  // (rowsResult.missing_in_source_rows), mirroring downloadMissingInTargetCsv.
  const downloadMissingInSourceCsv = () => {
    if (!rowsResult) return;
    const valueCols = fields
      .filter((f) => !f.isKey && f.targetField.trim())
      .map((f) => f.targetField.trim());
    const header = [...rowsResult.key_columns, ...valueCols];
    const rows = rowsResult.missing_in_source_rows.map((r) => [
      ...rowsResult.key_columns.map((k) => r.key[k] ?? ""),
      ...valueCols.map((c) => r.values[c] ?? ""),
    ]);
    const csv = [header, ...rows]
      .map((row) => row.map(csvEscape).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `missing_in_source_${filenameTimestamp()}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <button
          onClick={onBackToMappingChoice}
          className="ztd-btn ztd-btn-secondary"
        >
          <ArrowLeft size={14} /> Back to Mapping Choice
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        <div className="space-y-4">
          <SectionCard title="Source System">
            {sourceConnectionId ? (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm text-green-400">
                  <CheckCircle2 size={14} />
                  {sourceConnLabel || "saved connection"}
                </div>
                <button
                  onClick={() => setShowSourceConnectDialog(true)}
                  className="ztd-btn ztd-btn-secondary"
                >
                  <RefreshCw size={14} />
                  Change connection
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowSourceConnectDialog(true)}
                  className="ztd-btn ztd-btn-primary"
                >
                  <RefreshCw size={14} />
                  Connect Source System
                </button>
                <span className="text-xs text-white/40">
                  Any database this reconciliation should read as the source.
                </span>
              </div>
            )}
            <ConnectDatabaseDialog
              open={showSourceConnectDialog}
              onClose={() => setShowSourceConnectDialog(false)}
              title="Connect Source System"
              projectId={projectId}
              kind="source"
              agentKey="data_converter"
              defaultDbType="oracle"
              testIdPrefix="converter-source-standalone-reconcile"
              onConnectExisting={handleSourceConnectExistingForLoad}
              onConnectNew={handleSourceConnectNew}
            />
          </SectionCard>
          {projectId && (
            <SavedConnectionsPanel
              projectId={projectId}
              kind="source"
              onActivated={handleSourceConnectionActivatedFromLibrary}
              onConnectExisting={handleSourceConnectExistingForLoad}
            />
          )}
        </div>

        <div className="space-y-4">
          <SectionCard title="Target System">
            {targetConnectionId ? (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 text-sm text-green-400">
                  <CheckCircle2 size={14} />
                  {targetConnLabel || "saved connection"}
                </div>
                <button
                  onClick={() => setShowTargetConnectDialog(true)}
                  className="ztd-btn ztd-btn-secondary"
                >
                  <RefreshCw size={14} />
                  Change connection
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setShowTargetConnectDialog(true)}
                  className="ztd-btn ztd-btn-primary"
                >
                  <RefreshCw size={14} />
                  Connect Target System
                </button>
                <span className="text-xs text-white/40">
                  Any database this reconciliation should read as the target.
                </span>
              </div>
            )}
            <ConnectDatabaseDialog
              open={showTargetConnectDialog}
              onClose={() => setShowTargetConnectDialog(false)}
              title="Connect Target System"
              projectId={projectId}
              kind="target"
              agentKey="data_converter"
              defaultDbType="oracle"
              testIdPrefix="converter-target-standalone-reconcile"
              onConnectExisting={handleTargetConnectExisting}
              onConnectNew={handleTargetConnectNew}
            />
          </SectionCard>
          {projectId && (
            <SavedConnectionsPanel
              projectId={projectId}
              kind="target"
              onActivated={handleTargetConnectionActivatedFromLibrary}
              onConnectExisting={handleTargetConnectExisting}
            />
          )}
        </div>
      </div>

      <SectionCard title="Tables">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="space-y-3">
            <p className="text-xs font-semibold text-white/60">Source</p>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] text-white/40">
                  Schema (optional)
                </label>
                <div className="flex items-center gap-2">
                  {srcSchemas.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSrcSchemaManual((v) => !v)}
                      className="text-[11px] text-brand-400 hover:text-brand-300"
                    >
                      {srcSchemaManual ? "Choose from list" : "Type manually"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => void handleListSrcSchemas()}
                    disabled={!sourceConnectionId || srcSchemasLoading}
                    title="List schemas on the connected source"
                    className="flex items-center gap-1 text-[11px] text-white/40 hover:text-white/70 disabled:opacity-40"
                  >
                    {srcSchemasLoading ? (
                      <Loader2 size={10} className="animate-spin" />
                    ) : (
                      <RefreshCw size={10} />
                    )}
                    {srcSchemas.length > 0 ? "Refresh" : "List"}
                  </button>
                </div>
              </div>
              {srcSchemaManual || srcSchemas.length === 0 ? (
                <input
                  type="text"
                  value={srcSchema}
                  onChange={(e) => setSrcSchema(e.target.value)}
                  placeholder="Schema (optional)"
                  className="w-full px-2.5 py-1.5 bg-surface-400/50 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-brand-500/50 transition"
                />
              ) : (
                <select
                  value={srcSchema}
                  onChange={(e) => {
                    setSrcSchema(e.target.value);
                    setSrcTable("");
                    setSrcTables([]);
                  }}
                  className="w-full px-2.5 py-1.5 bg-surface-400/50 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-brand-500/50 transition"
                >
                  <option value="">Select schema…</option>
                  {srcSchemas.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] text-white/40">Table name</label>
                <div className="flex items-center gap-2">
                  {srcTables.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setSrcTableManual((v) => !v)}
                      className="text-[11px] text-brand-400 hover:text-brand-300"
                    >
                      {srcTableManual ? "Choose from list" : "Type manually"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => void handleListSrcTables(srcSchema)}
                    disabled={!sourceConnectionId || srcTablesLoading}
                    title="List tables in this schema on the connected source"
                    className="flex items-center gap-1 text-[11px] text-white/40 hover:text-white/70 disabled:opacity-40"
                  >
                    {srcTablesLoading ? (
                      <Loader2 size={10} className="animate-spin" />
                    ) : (
                      <RefreshCw size={10} />
                    )}
                    {srcTables.length > 0 ? "Refresh" : "List"}
                  </button>
                </div>
              </div>
              {srcTableManual || srcTables.length === 0 ? (
                <input
                  type="text"
                  value={srcTable}
                  onChange={(e) => setSrcTable(e.target.value)}
                  placeholder="Table name"
                  className="w-full px-2.5 py-1.5 bg-surface-400/50 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-brand-500/50 transition"
                />
              ) : (
                <select
                  value={srcTable}
                  onChange={(e) => setSrcTable(e.target.value)}
                  className="w-full px-2.5 py-1.5 bg-surface-400/50 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-brand-500/50 transition"
                >
                  <option value="">Select table…</option>
                  {srcTables.map((t) => (
                    <option key={t.table_name} value={t.table_name}>
                      {t.table_name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
          <div className="space-y-3">
            <p className="text-xs font-semibold text-white/60">Target</p>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] text-white/40">
                  Schema (optional)
                </label>
                <div className="flex items-center gap-2">
                  {tgtSchemas.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setTgtSchemaManual((v) => !v)}
                      className="text-[11px] text-brand-400 hover:text-brand-300"
                    >
                      {tgtSchemaManual ? "Choose from list" : "Type manually"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => void handleListTgtSchemas()}
                    disabled={!targetConnectionId || tgtSchemasLoading}
                    title="List schemas on the connected target"
                    className="flex items-center gap-1 text-[11px] text-white/40 hover:text-white/70 disabled:opacity-40"
                  >
                    {tgtSchemasLoading ? (
                      <Loader2 size={10} className="animate-spin" />
                    ) : (
                      <RefreshCw size={10} />
                    )}
                    {tgtSchemas.length > 0 ? "Refresh" : "List"}
                  </button>
                </div>
              </div>
              {tgtSchemaManual || tgtSchemas.length === 0 ? (
                <input
                  type="text"
                  value={tgtSchema}
                  onChange={(e) => setTgtSchema(e.target.value)}
                  placeholder="Schema (optional)"
                  className="w-full px-2.5 py-1.5 bg-surface-400/50 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-brand-500/50 transition"
                />
              ) : (
                <select
                  value={tgtSchema}
                  onChange={(e) => {
                    setTgtSchema(e.target.value);
                    setTgtTable("");
                    setTgtTables([]);
                  }}
                  className="w-full px-2.5 py-1.5 bg-surface-400/50 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-brand-500/50 transition"
                >
                  <option value="">Select schema…</option>
                  {tgtSchemas.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="text-[11px] text-white/40">Table name</label>
                <div className="flex items-center gap-2">
                  {tgtTables.length > 0 && (
                    <button
                      type="button"
                      onClick={() => setTgtTableManual((v) => !v)}
                      className="text-[11px] text-brand-400 hover:text-brand-300"
                    >
                      {tgtTableManual ? "Choose from list" : "Type manually"}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => void handleListTgtTables(tgtSchema)}
                    disabled={!targetConnectionId || tgtTablesLoading}
                    title="List tables in this schema on the connected target"
                    className="flex items-center gap-1 text-[11px] text-white/40 hover:text-white/70 disabled:opacity-40"
                  >
                    {tgtTablesLoading ? (
                      <Loader2 size={10} className="animate-spin" />
                    ) : (
                      <RefreshCw size={10} />
                    )}
                    {tgtTables.length > 0 ? "Refresh" : "List"}
                  </button>
                </div>
              </div>
              {tgtTableManual || tgtTables.length === 0 ? (
                <input
                  type="text"
                  value={tgtTable}
                  onChange={(e) => setTgtTable(e.target.value)}
                  placeholder="Table name"
                  className="w-full px-2.5 py-1.5 bg-surface-400/50 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-brand-500/50 transition"
                />
              ) : (
                <select
                  value={tgtTable}
                  onChange={(e) => setTgtTable(e.target.value)}
                  className="w-full px-2.5 py-1.5 bg-surface-400/50 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-brand-500/50 transition"
                >
                  <option value="">Select table…</option>
                  {tgtTables.map((t) => (
                    <option key={t.table_name} value={t.table_name}>
                      {t.table_name}
                    </option>
                  ))}
                </select>
              )}
            </div>
          </div>
        </div>
      </SectionCard>

      <SectionCard
        title="Key & Compare Fields"
        actions={
          <button
            onClick={() =>
              setFields((prev) => [
                ...prev,
                {
                  id: nextFieldId(),
                  sourceColumn: "",
                  targetField: "",
                  isKey: false,
                },
              ])
            }
            className="ztd-btn ztd-btn-secondary ztd-btn-sm"
          >
            <Plus size={13} /> Add Field
          </button>
        }
      >
        <p className="text-xs text-white/50 mb-3">
          List every field to compare between the source and target tables, and
          mark the field(s) that uniquely identify a row (used to match rows for
          row-by-row reconciliation).
        </p>
        <div className="space-y-2">
          <div className="grid grid-cols-[1fr_1fr_80px_32px] gap-2 text-xs font-semibold text-white/40 px-1">
            <span>Source Column</span>
            <span>Target Field</span>
            <span>Key</span>
            <span />
          </div>
          {fields.map((f) => (
            <div
              key={f.id}
              className="grid grid-cols-[1fr_1fr_80px_32px] gap-2 items-center"
            >
              {f.sourceManual || srcColumns.length === 0 ? (
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    value={f.sourceColumn}
                    onChange={(e) =>
                      setFields((prev) =>
                        prev.map((x) =>
                          x.id === f.id
                            ? { ...x, sourceColumn: e.target.value }
                            : x,
                        ),
                      )
                    }
                    placeholder="e.g. CUST_ID"
                    className="flex-1 px-2.5 py-1.5 bg-surface-400/50 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-brand-500/50 transition"
                  />
                  {srcColumns.length > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setFields((prev) =>
                          prev.map((x) =>
                            x.id === f.id ? { ...x, sourceManual: false } : x,
                          ),
                        )
                      }
                      title="Choose from list"
                      className="text-white/30 hover:text-white/60"
                    >
                      <ListChecks size={13} />
                    </button>
                  )}
                </div>
              ) : (
                <select
                  value={f.sourceColumn}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFields((prev) =>
                      prev.map((x) =>
                        x.id === f.id
                          ? v === "__manual__"
                            ? { ...x, sourceManual: true }
                            : { ...x, sourceColumn: v }
                          : x,
                      ),
                    );
                  }}
                  className="px-2.5 py-1.5 bg-surface-400/50 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-brand-500/50 transition"
                >
                  <option value="">Select column…</option>
                  {srcColumns.map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                  <option value="__manual__">Type manually…</option>
                </select>
              )}
              {f.targetManual || tgtColumns.length === 0 ? (
                <div className="flex items-center gap-1">
                  <input
                    type="text"
                    value={f.targetField}
                    onChange={(e) =>
                      setFields((prev) =>
                        prev.map((x) =>
                          x.id === f.id
                            ? { ...x, targetField: e.target.value }
                            : x,
                        ),
                      )
                    }
                    placeholder="e.g. customer_id"
                    className="flex-1 px-2.5 py-1.5 bg-surface-400/50 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-brand-500/50 transition"
                  />
                  {tgtColumns.length > 0 && (
                    <button
                      type="button"
                      onClick={() =>
                        setFields((prev) =>
                          prev.map((x) =>
                            x.id === f.id ? { ...x, targetManual: false } : x,
                          ),
                        )
                      }
                      title="Choose from list"
                      className="text-white/30 hover:text-white/60"
                    >
                      <ListChecks size={13} />
                    </button>
                  )}
                </div>
              ) : (
                <select
                  value={f.targetField}
                  onChange={(e) => {
                    const v = e.target.value;
                    setFields((prev) =>
                      prev.map((x) =>
                        x.id === f.id
                          ? v === "__manual__"
                            ? { ...x, targetManual: true }
                            : { ...x, targetField: v }
                          : x,
                      ),
                    );
                  }}
                  className="px-2.5 py-1.5 bg-surface-400/50 border border-white/10 rounded-lg text-xs text-white focus:outline-none focus:border-brand-500/50 transition"
                >
                  <option value="">Select column…</option>
                  {tgtColumns.map((c) => (
                    <option key={c.name} value={c.name}>
                      {c.name}
                    </option>
                  ))}
                  <option value="__manual__">Type manually…</option>
                </select>
              )}
              <label className="flex items-center justify-center">
                <input
                  type="checkbox"
                  checked={f.isKey}
                  onChange={(e) =>
                    setFields((prev) =>
                      prev.map((x) =>
                        x.id === f.id ? { ...x, isKey: e.target.checked } : x,
                      ),
                    )
                  }
                />
              </label>
              <button
                onClick={() =>
                  setFields((prev) =>
                    prev.length > 1 ? prev.filter((x) => x.id !== f.id) : prev,
                  )
                }
                disabled={fields.length <= 1}
                className="text-white/30 hover:text-red-400 disabled:opacity-30 disabled:cursor-not-allowed transition"
                title="Remove field"
              >
                <Trash2 size={14} />
              </button>
            </div>
          ))}
        </div>
      </SectionCard>

      {error && (
        <div className="flex items-center gap-2 text-xs text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2">
          <AlertTriangle size={14} />
          {error}
        </div>
      )}

      <SectionCard
        title="Run Reconciliation"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <button
              onClick={() => void handleReconcileCounts()}
              disabled={countsLoading}
              className="ztd-btn ztd-btn-secondary ztd-btn-sm"
            >
              {countsLoading ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <GitCompareArrows size={13} />
              )}
              Run Count Check
            </button>
            <button
              onClick={() => void handleReconcileRows()}
              disabled={rowsLoading}
              className="ztd-btn ztd-btn-primary ztd-btn-sm"
            >
              {rowsLoading ? (
                <Loader2 size={13} className="animate-spin" />
              ) : (
                <Scale size={13} />
              )}
              Run Row-by-Row Reconciliation
            </button>
          </div>
        }
      >
        {!countsResult && !rowsResult && (
          <p className="text-xs text-white/40">
            Results will appear here after running a check above.
          </p>
        )}

        {(countsResult || rowsResult) && (
          <div className="flex items-center gap-1 mb-3 border-b border-white/10">
            {countsResult && (
              <button
                onClick={() => setActiveResultTab("counts")}
                className={`px-3 py-1.5 text-xs font-medium border-b-2 transition ${
                  activeResultTab === "counts"
                    ? "border-brand-400 text-white"
                    : "border-transparent text-white/40 hover:text-white/70"
                }`}
              >
                Count Check
              </button>
            )}
            {rowsResult && (
              <button
                onClick={() => setActiveResultTab("rows")}
                className={`px-3 py-1.5 text-xs font-medium border-b-2 transition ${
                  activeResultTab === "rows"
                    ? "border-brand-400 text-white"
                    : "border-transparent text-white/40 hover:text-white/70"
                }`}
              >
                Row-by-Row Reconciliation
              </button>
            )}
          </div>
        )}

        {activeResultTab === "counts" && countsResult && (
          <div className="mb-4">
            <p className="text-xs font-semibold text-white/60 mb-2">
              Count Check
            </p>
            <div
              className={`grid grid-cols-2 sm:grid-cols-3 gap-4 p-4 rounded-xl border ${
                countsResult.match
                  ? "bg-green-500/10 border-green-500/20"
                  : "bg-amber-500/10 border-amber-500/20"
              }`}
            >
              <div>
                <div className="text-white/40 text-xs">Source Count</div>
                <div className="text-white font-semibold">
                  {countsResult.source_count ?? "—"}
                </div>
              </div>
              <div>
                <div className="text-white/40 text-xs">Target Count</div>
                <div className="text-white font-semibold">
                  {countsResult.target_count ?? "—"}
                </div>
              </div>
              <div>
                <div className="text-white/40 text-xs">Status</div>
                <div className="flex items-center gap-1.5 font-semibold">
                  {countsResult.match ? (
                    <>
                      <CheckCircle2 size={14} className="text-green-400" />
                      <span className="text-green-400">Match</span>
                    </>
                  ) : (
                    <>
                      <AlertTriangle size={14} className="text-amber-400" />
                      <span className="text-amber-400">
                        Mismatch (diff: {countsResult.difference})
                      </span>
                    </>
                  )}
                </div>
              </div>
              {countsResult.error && (
                <div className="col-span-2 sm:col-span-3 text-xs text-red-300">
                  {countsResult.error}
                </div>
              )}
            </div>
          </div>
        )}

        {activeResultTab === "rows" && rowsResult && (
          <div>
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <p className="text-xs font-semibold text-white/60">
                {`Row-by-Row Reconciliation — ${rowsResult.matched_rows} matched / ${rowsResult.mismatched_rows} mismatched / ${rowsResult.missing_in_target} missing in target / ${rowsResult.missing_in_source} missing in source`}
              </p>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  onClick={downloadMismatchesCsv}
                  disabled={rowsResult.mismatches.length === 0}
                  className="ztd-btn ztd-btn-secondary ztd-btn-sm disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Download CSV: source vs. target value for every mismatched field"
                >
                  <Download size={12} /> Mismatches Report
                </button>
                <button
                  onClick={downloadMissingInTargetCsv}
                  disabled={rowsResult.missing_in_target_rows.length === 0}
                  className="ztd-btn ztd-btn-secondary ztd-btn-sm disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Download CSV: source records not matching target by key"
                >
                  <Download size={12} /> Missing-in-Target Report
                </button>
                <button
                  onClick={downloadMissingInSourceCsv}
                  disabled={rowsResult.missing_in_source_rows.length === 0}
                  className="ztd-btn ztd-btn-secondary ztd-btn-sm disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Download CSV: target records not matching source by key"
                >
                  <Download size={12} /> Missing-in-Source Report
                </button>
                <button
                  onClick={downloadFieldStatsCsv}
                  disabled={rowsResult.field_stats.length === 0}
                  className="ztd-btn ztd-btn-secondary ztd-btn-sm disabled:opacity-30 disabled:cursor-not-allowed"
                  title="Download CSV: per-field matching vs. non-matching record counts"
                >
                  <Download size={12} /> Field Match Report
                </button>
              </div>
            </div>
            <p className="text-xs text-white/50 mb-3">
              Rows are matched by key, not by position: every{" "}
              <span className="text-white/60">source</span> row's key is looked
              up in the target. If found, the two rows' other (non-key) field
              values are compared —{" "}
              <span className="text-green-400">Matched</span> means every
              compared field is identical,{" "}
              <span className="text-amber-400">Mismatched</span> means at least
              one field differs.{" "}
              <span className="text-white/60">Missing in target</span> means the
              source key has no corresponding target row at all. —{" "}
              {rowsResult.source_total_rows} source rows scanned /{" "}
              {rowsResult.target_total_rows} target rows scanned. Full mismatch
              and missing-row detail is available via the report buttons above.
            </p>
            {rowsResult.truncated && (
              <div className="mb-3 p-2 rounded-lg bg-amber-500/10 border border-amber-500/20 text-xs text-amber-300 flex items-center gap-2">
                <AlertTriangle size={12} /> Scan capped at{" "}
                {rowsResult.max_rows_scanned.toLocaleString()} rows per side —
                results reflect only the scanned portion, not necessarily the
                whole table.
              </div>
            )}
            {rowsResult.source_total_rows > 0 &&
              rowsResult.mismatched_rows === 0 &&
              rowsResult.missing_in_target === 0 && (
                <div className="mb-3 p-2 rounded-lg bg-green-500/10 border border-green-500/20 text-xs text-green-400 flex items-center gap-2">
                  <CheckCircle2 size={12} /> Matched — every source record was
                  found in the target with matching values.
                  {rowsResult.missing_in_source > 0 && (
                    <span className="text-white/40">
                      {" "}
                      (target also has{" "}
                      {rowsResult.missing_in_source.toLocaleString()} record
                      {rowsResult.missing_in_source === 1 ? "" : "s"} not
                      present in source)
                    </span>
                  )}
                </div>
              )}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 p-4 rounded-xl border bg-white/5 border-white/10 mb-3">
              <div>
                <div className="text-white/40 text-xs">Matched</div>
                <div className="text-lg font-semibold text-green-400">
                  {rowsResult.matched_rows}
                </div>
              </div>
              <div>
                <div className="text-white/40 text-xs">Mismatched</div>
                <div className="text-lg font-semibold text-amber-400">
                  {rowsResult.mismatched_rows}
                </div>
              </div>
              <div>
                <div className="text-white/40 text-xs">Missing in Target</div>
                <div className="text-lg font-semibold text-red-400">
                  {rowsResult.missing_in_target}
                </div>
              </div>
              <div>
                <div className="text-white/40 text-xs">Missing in Source</div>
                <div className="text-lg font-semibold text-red-400">
                  {rowsResult.missing_in_source}
                </div>
              </div>
            </div>
            {(() => {
              const uniqueSourceKeys =
                rowsResult.matched_rows +
                rowsResult.mismatched_rows +
                rowsResult.missing_in_target;
              const dupSourceKeys =
                rowsResult.source_total_rows - uniqueSourceKeys;
              return dupSourceKeys > 0 ? (
                <div className="flex items-start gap-2 text-xs text-amber-400 bg-amber-500/10 border border-amber-500/20 rounded-lg px-3 py-2 mb-3">
                  <AlertTriangle size={14} className="mt-0.5 shrink-0" />
                  <span>
                    Your key field(s) only produced {uniqueSourceKeys} unique
                    value(s) across {rowsResult.source_total_rows} source rows —{" "}
                    {dupSourceKeys} row(s) share a key with another row and were
                    collapsed into one before comparison, so these stats
                    describe unique keys, not every raw row. Pick a field (or
                    field combination) that's actually unique per row — e.g. an
                    ID column — for an exact row-level reconciliation.
                  </span>
                </div>
              ) : null;
            })()}
            {rowsResult.field_stats.length > 0 && (
              <div className="overflow-auto max-h-[30vh] mb-3">
                <p className="text-xs text-white/50 mb-1">
                  Per-field match / mismatch (across every row with a matching
                  key, {rowsResult.matched_rows + rowsResult.mismatched_rows}{" "}
                  total):
                </p>
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-white/40">
                      <th className="px-2 py-1 font-semibold">Source Column</th>
                      <th className="px-2 py-1 font-semibold">Target Field</th>
                      <th className="px-2 py-1 font-semibold text-right">
                        Matching
                      </th>
                      <th className="px-2 py-1 font-semibold text-right">
                        Not Matching
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {rowsResult.field_stats.map((s) => (
                      <tr
                        key={s.target_field}
                        className="border-t border-white/5"
                      >
                        <td className="px-2 py-1 font-mono text-white/80">
                          {s.source_column}
                        </td>
                        <td className="px-2 py-1 font-mono text-brand-300/80">
                          {s.target_field}
                        </td>
                        <td className="px-2 py-1 text-right text-green-400">
                          {s.matched_count}
                        </td>
                        <td className="px-2 py-1 text-right text-amber-400">
                          {s.mismatched_count}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </SectionCard>
    </div>
  );
}
